import * as Sentry from "@sentry/react";
import ApiClient from "./generated/src/ApiClient";
import { markApiErrorReported, shouldSkipAuthOrCacheErrorReport } from "./sentryApiErrorPolicy";

type CallApiCallback = (error: object | null, data: unknown, response: SuperAgentResponse) => void;

type SuperAgentResponse = {
  status?: number;
  body?: {
    code?: string;
    message?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

const getAppVersion = (): string | null =>
  process.env.REACT_APP_VERSION || process.env.REACT_APP_GIT_COMMIT_HASH || null;

const EMAIL_TAKEN_ERROR_MESSAGE = "Email has already been taken";
const COMPANY_ALREADY_MATCHED_CODE = "COMPANY_ALREADY_MATCHED";
// 生成クライアントが callApi に渡すパステンプレートそのまま
const FILTER_HISTORY_ITEM_PATH = "/agent/v1/filter_histories/{id}";

// response.body.errors がメールアドレス重複エラーのみかどうかを判定する
const hasOnlyEmailTakenErrors = (response: SuperAgentResponse): boolean => {
  const errors: unknown = response?.body?.errors;
  if (!Array.isArray(errors)) return false;
  return errors.length > 0 && errors.every((error) => error === EMAIL_TAKEN_ERROR_MESSAGE);
};

// console と共有する認証・キャッシュの除外は sentryApiErrorPolicy.ts に定義する。
// パス・本文・通信エラー等の interceptor 固有の除外はここに追加する。
export const shouldSkipSentryReport = (
  error: object,
  response: SuperAgentResponse,
  path: string,
  method: string
): boolean => {
  const status = response?.status;
  if (shouldSkipAuthOrCacheErrorReport(status, response?.body?.code)) return true;
  // ネットワークエラー（response なし、またはステータスなし）はコードで対処できないため除外
  if (!response?.status) return true;
  // 楽観的ロックの競合（他ユーザーが先に更新済み）は正常なビジネスロジックのため除外
  if (status === 409) return true;
  // ログイン失敗は正常な動作のため除外
  if (path === "/agent/v1/login" && status === 422) return true;
  // メールアドレス重複登録は正常な動作のため除外
  if (path === "/agent/v1/registrations" && status === 422) return true;
  // プロフィール更新時のメールアドレス重複は正常な動作のため除外（他のバリデーションエラーは送信する）
  if (path === "/agent/v1/agent" && status === 422 && hasOnlyEmailTakenErrors(response)) return true;
  // 単発オファー送信先の会社が既にユーザーとマッチ済み（同じ会社の別プランナー経由を含む）は
  // 画面で理由を案内済みの想定内の応答のため除外
  if (status === 422 && response?.body?.code === COMPANY_ALREADY_MATCHED_CODE) return true;
  // 絞り込み履歴の削除で対象が既に存在しない（別タブ・別端末で削除済み、連打）場合は、
  // フロントで一覧から除去して冪等に成功扱いにするため除外（PROPALLY_SECOND_WEB-2F）。404 全般は除外しない
  if (String(method).toUpperCase() === "DELETE" && path === FILTER_HISTORY_ITEM_PATH && status === 404) return true;
  return false;
};

// 会社マッチ済み 422 は、API 呼び出し側の console.error(error) を captureConsoleIntegration が拾う経路でも
// Sentry に届く（shouldSkipSentryReport は interceptor 自身の captureException しか止めない）。
// Sentry.init の beforeSend で hint.originalException（superagent のエラー）を判定して落とすための関数
export const isCompanyAlreadyMatchedException = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const { status, response } = error as { status?: unknown; response?: { body?: { code?: unknown } } | null };
  return status === 422 && response?.body?.code === COMPANY_ALREADY_MATCHED_CODE;
};

// Sentry に載せる API 応答の情報。
// リクエスト本文と応答本体はパスワード・顧客の氏名や連絡先・年収などを含むため送らない。
// 調査に必要な status・エラーコード・メッセージ・検証エラー（errors）だけを送る
export const buildSentryApiErrorContext = (
  response: SuperAgentResponse | null | undefined
): { tags: Record<string, string>; extras: Record<string, unknown> } => {
  const tags: Record<string, string> = {};
  const extras: Record<string, unknown> = {};

  if (response?.status) {
    tags["http.status"] = String(response.status);
  }
  const body = response?.body;
  if (typeof body === "object" && body !== null) {
    if (typeof body.code === "string" && body.code) {
      tags["api.error_code"] = body.code;
    }
    if (body.message) {
      extras["api.error_message"] = body.message;
    }
    if (body.errors) {
      extras["api.errors"] = body.errors;
    }
  }

  return { tags, extras };
};

// 生成された ApiClient の callApi をラップして共通で Sentry にエラーを報告する
export const initApiSentryInterceptor = () => {
  const originalCallApi = ApiClient.instance.callApi.bind(ApiClient.instance);

  ApiClient.instance.callApi = function (
    path: string,
    httpMethod: string,
    pathParams: Record<string, string>,
    queryParams: Record<string, object>,
    headerParams: Record<string, object>,
    formParams: Record<string, object>,
    bodyParam: object | null,
    authNames: string[],
    contentTypes: string[],
    accepts: string[],
    returnType: string | (new (...a: unknown[]) => unknown) | unknown[],
    apiBasePath: string,
    callback: CallApiCallback
  ) {
    const wrappedCallback: CallApiCallback = (error, data, response) => {
      if (error && !shouldSkipSentryReport(error, response, path, httpMethod)) {
        Sentry.withScope((scope) => {
          const url = ApiClient.instance.buildUrl(path, pathParams, apiBasePath);

          if (httpMethod) {
            scope.setTag("http.method", String(httpMethod).toUpperCase());
          }

          if (url) {
            scope.setExtra("request.url", url);
          }

          const { tags, extras } = buildSentryApiErrorContext(response);
          Object.entries(tags).forEach(([key, value]) => scope.setTag(key, value));
          Object.entries(extras).forEach(([key, value]) => scope.setExtra(key, value));

          const appVersion = getAppVersion();
          if (appVersion) {
            // propally_web に合わせて app.version タグを送信する（環境変数はCI側で設定が必要）
            scope.setTag("app.version", appVersion);
          }

          const normalizePath = (path: string): string => path.replace(/\/\d+/g, "/:id");

          const method = String(httpMethod).toUpperCase();
          const normalizedPath = normalizePath(path);
          const status = response?.status;
          const errorCode = response?.body?.code;

          // エラーのタイトルをAPIエラーと分かるように生成
          const label = [`[${method} ${normalizedPath}]`, status ?? "network error", errorCode]
            .filter(Boolean)
            .join(" ");

          const sentryError = new Error(String(label));
          sentryError.name = "ApiError";

          scope.setFingerprint([method, normalizedPath, String(status ?? "network_error")]);

          Sentry.captureException(sentryError);
          markApiErrorReported(error);
          markApiErrorReported(response);
        });
      }
      callback(error, data, response);
    };

    return originalCallApi(
      path,
      httpMethod,
      pathParams,
      queryParams,
      headerParams,
      formParams,
      bodyParam,
      authNames,
      contentTypes,
      accepts,
      returnType,
      apiBasePath,
      wrappedCallback
    );
  };
};
